import { Button, ButtonGroup } from "@mui/material";
import { addHours } from "date-fns";
import { where } from "firebase/firestore";
import { cloneDeep } from "lodash";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useAuthContext } from "../Auth";
import { useHouseContext } from "../HouseContext";
import { AutoCompleteUser } from "../components/UI/AutoCompleteUser";
import { ProgressContainer } from "../components/UI/ProgressContainer";
import { TableBookings } from "../components/UI/TableBookings";
import { DialogEditBooking } from "../dialogs/DialogEditBooking";
import { DBUser, ReservationStatus, RoomReservation } from "../ostertalertypes";
import { fetchAllocationBookings } from "../utils/database";
import { handleError } from "../utils/utils";
import { hasAccessToHouse, filterReservationsByAdminHouses } from "../utils/adminutils";

export const PageEditBookings: React.FC = () => {
  const houseCtx = useHouseContext();
  const authCtx = useAuthContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const [reservations, setReservations] = useState<RoomReservation[]>([]);
  const [selectedUser, setSelectedUser] = useState<DBUser | null>(null);
  const [selectedItem, setSelectedItem] = useState<RoomReservation | null>(null);
  const [isDialogEditOpen, setDialogEditOpen] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);

  useEffect(() => {
    async function fetchBookings() {
      if (selectedUser === null) {
        return;
      }
      try {
        setIsLoading(true);
        const myReservations = await fetchAllocationBookings(true,
          where("userid", "==", selectedUser?.userid)
          // where("status", "in", [ReservationStatus.ACCEPTED]) allow any kind of bookings to be modified
        );
        // Filter by admin's accessible houses
        const filteredReservations = filterReservationsByAdminHouses(myReservations, authCtx.adminHouses);
        const sortedAsc = filteredReservations.sort(
          (objA, objB) => objB.from?.toMillis()! - objA.from?.toMillis()!,
        );
        setReservations(sortedAsc);
        setIsLoading(false);
      } catch (error) {
        handleError("Fehler beim Abrufen der Reservierungen", error, enqueueSnackbar, setIsLoading);
      }
    }
    fetchBookings();
  }, [selectedUser, reload, enqueueSnackbar, authCtx.adminHouses]);

  function getActionBtn(res: RoomReservation) {
    let disabled = false;

    // Check if admin has access to this house
    if (!hasAccessToHouse(authCtx.adminHouses, res.houseName)) {
      disabled = true;
    }

    if (res.to) {
      let latestCheckIn = addHours(res.to.toDate(), 12);
      if (latestCheckIn <= new Date()) {
        disabled = true; // reservation is in the past
      }
    }
    if (res.status === ReservationStatus.CANCELED) {
      disabled = true;
    }
    return (
      <ButtonGroup disabled={disabled} size="small" aria-label="small button group">
        <Button key="one" onClick={() => {
            setSelectedItem(cloneDeep(res));
            setDialogEditOpen(true);
          }}>
          Bearbeiten
        </Button>
      </ButtonGroup>
    );
  }

  const onUserSelected = (selection: DBUser | null) => {
    if (selection) {
      setSelectedUser(selection);
    }
  };

  const onEditDialogChanged = (appartment: RoomReservation, index: number) => {
    setSelectedItem(cloneDeep(appartment));
  };

  const onGuestCountChanged = (guestCount: number) => {
    if (selectedItem) {
      setSelectedItem({...selectedItem, guestCount: guestCount});
    }
  };

  const onEditDialogClose = (changed: boolean) => {
    setDialogEditOpen(false);
    setSelectedItem(null);
    if (changed) {
      setReload((oldVal) => !oldVal);
    }
  };

  return (
    <ProgressContainer size="max-w-5xl" isLoading={isLoading}>
      <h1 className="text-2xl mt-0 mb-10 text-gray-500 text-center md:text-left">Buchung ändern</h1>
      <AutoCompleteUser
        users={authCtx.allUsers}
        className="mb-10"
        selectedUser={selectedUser}
        onUserSelected={onUserSelected}
      ></AutoCompleteUser>
      <TableBookings getActionBtn={getActionBtn} roomReservation={reservations} showGuestName={true}></TableBookings>
      {selectedItem != null && isDialogEditOpen && (
        <DialogEditBooking
          house={houseCtx.houses.get(selectedItem.houseName.toLowerCase())!}
          allowCancel={true}
          open={isDialogEditOpen}
          onClose={onEditDialogClose}
          onBookingChanged={onEditDialogChanged}
          onGuestCountChanged={onGuestCountChanged}
          roomReservation={selectedItem}
        />
      )}
    </ProgressContainer>
  );
};
